import { ReactNode } from 'react';
import { useAuth } from '@/hooks/useAuth';
import { useModuleAccess } from '@/hooks/useModuleAccess';
import { useWorkspace } from '@/contexts/WorkspaceContext';
import NoAccess from '@/components/NoAccess';

interface ModuleGuardProps {
  module: string;
  children: ReactNode;
}

/**
 * ModuleGuard v2
 * 
 * Protects routes/components by checking if a module is enabled.
 * Uses useModuleAccess hook for all module checks.
 * Owners and super_admin bypass module checks.
 * If the module is disabled, shows Upgrade Required UI with link to /admin/modules.
 * If enabled, renders children.
 * 
 * @param module - The module code to check (e.g., "bill_collection")
 * @param children - The content to render if module is enabled
 */
export default function ModuleGuard({ module, children }: ModuleGuardProps) {
  // ============================================
  // HOOKS - MUST BE CALLED UNCONDITIONALLY
  // ============================================
  const { user } = useAuth();
  const { currentWorkspace } = useWorkspace();
  const moduleAccess = useModuleAccess('workspace', currentWorkspace?.id || '');

  // ============================================
  // ACCESS CONTROL LOGIC - AFTER HOOKS
  // ============================================
  
  // Owners and super_admin bypass module checks
  const isSuperAdmin = user?.role === 'super_admin';
  const isOwner = moduleAccess.isOwner;
  const canBypass = isSuperAdmin || isOwner;

  // Show loading state while checking modules
  if (moduleAccess.isLoading) {
    return (
      <div className="flex items-center justify-center min-h-[400px]">
        <div className="text-center space-y-2">
          <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary mx-auto"></div>
          <p className="text-sm text-muted-foreground">Checking module access...</p>
        </div>
      </div>
    );
  }

  // Owners and super_admin always have access (bypass all checks)
  if (canBypass) {
    return <>{children}</>;
  }

  // Check if module is enabled using useModuleAccess
  // No hardcoded module allows - all checks go through the hook
  const hasAccess = moduleAccess.hasModule(module);

  // If module is disabled, show upgrade required UI
  if (!hasAccess) {
    return <NoAccess module={module} showUpgrade={true} />;
  }

  // Module is enabled - render children
  return <>{children}</>;
}
